import { memo, useCallback, useMemo } from 'react';
import { List } from '@divlab/divanui';

import * as Meta from '@Queries/Meta';
import * as PageProductStore from '@Stores/PageProduct';
import { useDeps } from '@Contexts/DI';
import MainSelect from '@UI/MainSelect';
import StringParameter from '../StringParameter';
import SampleParameter from '../SampleParameter';
import Dimension from '../Dimension';
import Hardness from '../Hardness';
import styles from './Column.module.css';

import type { FC, HTMLAttributes } from 'react';
import type { SelectItemData } from '@divlab/divanui';
import type { Parameter } from '@Pages/PageProduct';
import type { SizesVariantsData } from '../../Characteristics';

type Items = Record<Parameter['theme'], Parameter[]>;

interface Option {
  groupId: number;
  variantId: number;
}

export interface ColumnProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  parameters: Parameter[];
}

const Column: FC<ColumnProps> = (props) => {
  const { parameters, ...restProps } = props;
  const meta = Meta.useMeta();
  const { analytics } = useDeps();

  const handleChangeSizes = useCallback(
    (e: MouseEvent, items: SelectItemData[]) => {
      if (items[0]?.selected) return;

      analytics.dispatchEvent('sizes.changeSelected');
    },
    [analytics],
  );

  const handleChangeParameter = useCallback((_e, items: SelectItemData<Option>[]) => {
    items.forEach((item) => {
      PageProductStore.selectParameter(item.data);
    });
  }, []);

  const items = useMemo(() => {
    const result: Items = {
      default: [],
      dropdown: [],
      dimension: [],
      circle: [],
      hardness: [],
      mechanism: [],
    };

    parameters.forEach((parameter) => {
      if (!result[parameter.theme]) result[parameter.theme] = [];

      result[parameter.theme].push(parameter);
    });

    return result;
  }, [parameters]);

  const changeableOptions = useMemo(() => {
    return parameters.filter((parameter) => {
      return parameter.theme === 'dropdown' && ![2, 3].includes(parameter.groupId);
    });
  }, [parameters]);

  const sizesVariants: SizesVariantsData = useMemo(() => {
    const sizeParameters = parameters.find(
      (param) => (param.groupId === 2 || param.groupId === 3) && param.variants?.length > 1,
    );

    if (!sizeParameters) return null;

    if (!sizeParameters.variants) {
      return {
        title: sizeParameters.name,
        items: [
          {
            id: sizeParameters.groupId.toString(),
            title: sizeParameters.name,
            name: sizeParameters.values.map((item) => item.value.match(/\d+/)).join(' x '),
          },
        ],
      };
    }

    return {
      title: sizeParameters.name,
      items: sizeParameters.variants.map((variant) => ({
        id: variant.productId.toString(),
        title: `${variant.name}${variant.unit ? variant.unit.name : ''}`,
        name: `${variant.name}${variant.unit ? variant.unit.name : ''}`,
        href: variant.url,
        selected: !!variant.selected,
      })),
    };
  }, [parameters]);

  return (
    <div {...restProps}>
      {items.dimension.length > 0 && (
        <List
          className={styles.dimensions}
          items={items.dimension}
          renderChild={(parameter: Parameter) => (
            <Dimension
              className={styles.dimension}
              name={parameter.name}
              value={parameter.values}
            />
          )}
        />
      )}

      {sizesVariants && (
        <div>
          <MainSelect
            title={sizesVariants.title}
            items={sizesVariants.items}
            wide
            onChangeSelected={handleChangeSizes}
          />
        </div>
      )}

      {changeableOptions?.length > 0 && (
        <List
          className={styles.selects}
          items={changeableOptions}
          renderChild={(parameter: Parameter) => {
            const opts: SelectItemData[] = [];

            parameter.variants.forEach((variant) => {
              const id = variant.id ? variant.id : variant.productId;
              const price = variant.price && {
                value: variant.price,
                currency: meta.data?.currency,
              };

              opts.push({
                id: id.toString(),
                title: variant.name,
                name: variant.name,
                image: variant.image,
                href: variant.url,
                price,
                selected: !!variant.selected,
                data: {
                  groupId: parameter.groupId,
                  variantId: id,
                },
              });
            });

            return (
              <MainSelect
                wide
                className={styles.select}
                title={parameter.name}
                items={opts}
                bordered
                onChangeSelected={handleChangeParameter}
                withImage
              />
            );
          }}
        />
      )}

      {items.hardness.length > 0 && (
        <List
          className={styles.hardness}
          items={items.hardness}
          renderChild={(hardness: Parameter) => (
            <Hardness
              name={hardness.name}
              value={hardness.value}
              icon={hardness.icon}
              description={hardness.description}
            />
          )}
        />
      )}

      {[...items.mechanism, ...items.default].map((parameter, index) => (
        <StringParameter key={index} className={styles.parameter} parameter={parameter} />
      ))}

      {items.circle.map((parameter) =>
        parameter.variants.map((variant, index) => (
          <SampleParameter key={index} className={styles.parameter} material={variant} />
        )),
      )}
    </div>
  );
};

export default memo(Column);
